import React, { useEffect, useState } from 'react';
import { feedbackAPI, projectsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, PageHeader, Button, FormField, Table, Empty, Alert, Badge } from '../components/ui';
import { Send, Paperclip, MessageSquare } from 'lucide-react';
import FeedbackThreadModal from '../components/feedback/FeedbackThreadModal';
import API from '../services/api';

const CATEGORIES = ['General Feedback', 'Bug Fix / Correction', 'Change Request / Scope Update', 'General Inquiry'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];
const STATUSES = ['Open', 'In Review', 'In Progress', 'Needs Clarification', 'Resolved', 'Closed'];

const emptyForm = {
  project_id: '', category: 'General Feedback', module_name: '',
  subject: '', message: '', priority: 'Medium',
};

export default function Feedback() {
  const { user } = useAuth();
  const role = user?.role;
  const isClient = role === 'Client';
  const isManager = role === 'Admin' || role === 'Manager';

  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({ project_id: '', category: '', priority: '', status: '' });
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTicketId, setActiveTicketId] = useState(null);

  // client-only: new ticket form
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    projectsAPI.list().then((res) => setProjects(res.data.data || [])).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    setError('');
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const request = isClient ? feedbackAPI.listMine(params) : feedbackAPI.listAll(params);
    request
      .then((res) => setTickets(res.data.data || []))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load tickets'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (isClient || isManager) load(); /* eslint-disable-next-line */ }, [filters, role]);

  const uploadFiles = async () => {
    if (!files.length) return [];
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    const res = await API.post('/uploads/multiple', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data.data.map((f) => ({ url: f.url, originalname: f.originalname, mimetype: f.mimetype }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_id) { setError('Please select a project.'); return; }
    setSaving(true);
    setError('');
    try {
      const attachments = await uploadFiles();
      await feedbackAPI.create({ ...form, attachments });
      setForm((f) => ({ ...emptyForm, project_id: f.project_id }));
      setFiles([]);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error submitting ticket');
    } finally {
      setSaving(false);
    }
  };

  if (!isClient && !isManager) {
    return (
      <div className="animate-in">
        <PageHeader title="Feedback & Revisions" subtitle="This feature isn't available for your role" eyebrow="Feedback" />
        <Card><Empty message="Feedback is only available to clients and managers." /></Card>
      </div>
    );
  }

  const columns = [
    { key: 'ticket_number', label: 'Ticket ID', render: (t) => <span style={{ fontWeight: 700 }}>{t.ticket_number || `FB-${String(t.feedback_id).padStart(5, '0')}`}</span> },
    { key: 'subject', label: 'Subject', render: (t) => <span style={{ fontWeight: 600 }}>{t.subject}</span> },
    { key: 'project', label: 'Project', render: (t) => {
      const code = t.project?.project_code;
      const name = t.project?.project_name || `#${t.project_id}`;
      return code ? `${code} • ${name}` : name;
    } },
    ...(isManager ? [{ key: 'client', label: 'Client', render: (t) => t.client?.name || `#${t.client_id}` }] : []),
    { key: 'category', label: 'Category', render: (t) => <Badge value={t.category} /> },
    { key: 'module', label: 'Module', render: (t) => t.module_name || '—' },
    { key: 'priority', label: 'Priority', render: (t) => <Badge value={t.priority} /> },
    { key: 'status', label: 'Status', render: (t) => <Badge value={t.status} /> },
    { key: 'timestamp', label: 'Timestamp', render: (t) => new Date(t.createdAt).toLocaleString() },
  ];

  return (
    <div className="animate-in">
      <PageHeader
        title={isClient ? 'Feedback & Revisions' : 'Client Tickets'}
        subtitle={isClient ? 'Submit corrections, revision requests, or general feedback on your projects' : `${tickets.length} tickets`}
        eyebrow="Feedback"
      />

      {isClient && (
        <Card style={{ marginBottom: 24, padding: 24 }}>
          <Alert type="error" message={error} />
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 4 }}>
              <FormField label="Project">
                <select value={form.project_id} onChange={(e) => setForm((f) => ({ ...f, project_id: e.target.value }))} required>
                  <option value="">Select a project</option>
                  {projects.map((p) => (
                    <option key={p.project_id} value={p.project_id}>
                      {p.project_code ? `${p.project_code} • ${p.project_name}` : p.project_name}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Request Type">
                <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </FormField>
              <FormField label="Priority">
                <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </FormField>
            </div>
            <FormField label="Module / Page / Feature (optional)">
              <input
                value={form.module_name}
                onChange={(e) => setForm((f) => ({ ...f, module_name: e.target.value }))}
                placeholder="e.g. User Authentication Module"
              />
            </FormField>
            <FormField label="Subject">
              <input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} placeholder="Brief summary" required />
            </FormField>
            <FormField label="Details">
              <textarea rows={4} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} placeholder="Describe the issue, correction, or request in detail..." required />
            </FormField>
            <FormField label="Attachments (screenshots, PDFs)">
              <input type="file" multiple accept="image/*,.pdf" onChange={(e) => setFiles(Array.from(e.target.files))} />
              {files.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {files.map((f, i) => <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Paperclip size={12} />{f.name}</span>)}
                </div>
              )}
            </FormField>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" disabled={saving}><Send size={14} /> {saving ? 'Submitting...' : 'Submit Ticket'}</Button>
            </div>
          </form>
        </Card>
      )}

      <Card style={{ marginBottom: 20, display: 'flex', gap: 16, flexWrap: 'wrap', padding: 20 }}>
        <FormField label="Project">
          <select value={filters.project_id} onChange={(e) => setFilters((f) => ({ ...f, project_id: e.target.value }))}>
            <option value="">All Projects</option>
            {projects.map((p) => (
              <option key={p.project_id} value={p.project_id}>
                {p.project_code ? `${p.project_code} • ${p.project_name}` : p.project_name}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Category">
          <select value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}>
            <option value="">All</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </FormField>
        <FormField label="Priority">
          <select value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))}>
            <option value="">All</option>
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </FormField>
        <FormField label="Status">
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </FormField>
      </Card>

      <Card>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
        ) : tickets.length === 0 ? (
          <Empty message="No tickets found" />
        ) : (
          <Table
            columns={[...columns, { key: 'open', label: '', render: (t) => (
              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setActiveTicketId(t.feedback_id); }}>
                <MessageSquare size={13} /> Open Thread
              </Button>
            ) }]}
            data={tickets.map((t) => ({ ...t, id: t.feedback_id }))}
            onRowClick={(t) => setActiveTicketId(t.feedback_id)}
          />
        )}
      </Card>

      {activeTicketId && (
        <FeedbackThreadModal
          ticketId={activeTicketId}
          currentUser={user}
          isManager={isManager}
          onClose={() => setActiveTicketId(null)}
          onUpdated={load}
        />
      )}
    </div>
  );
}